import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/utils/axios";
import base from "../financePage.module.scss";
import cls from "./AllAccounts.module.scss";

const formatAmount = (value) => {
  const numeric = Number(value ?? 0);
  if (Number.isNaN(numeric)) return "0";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const AllAccounts = () => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const monthLabel = useMemo(
    () =>
      new Date().toLocaleString(undefined, {
        month: "long",
        year: "numeric",
      }),
    [],
  );

  useEffect(() => {
    let isMounted = true;

    const loadSummary = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await api.get("/v1/admin/finance/accounts/summary");
        if (!isMounted) return;
        setSummary(res.data?.data ?? null);
      } catch (err) {
        if (!isMounted) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load account summary",
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadSummary();
    return () => {
      isMounted = false;
    };
  }, []);

  const cards = [
    {
      key: "bank",
      tone: "cyan",
      title: "Bank account",
      meta: "Current balance",
      noty: summary?.bank?.balanceNoty,
      cad: summary?.bank?.balanceCad,
      link: "/admin/finance/accounts/bank",
      linkLabel: "Open bank ledger",
      icon: "🏦",
    },
    {
      key: "marketing-expense",
      tone: "pink",
      title: "Marketing expense",
      meta: `This month (${monthLabel})`,
      noty: summary?.marketingExpense?.totalNoty,
      cad: summary?.marketingExpense?.totalCad,
      link: "/admin/finance/accounts/marketing-expense",
      linkLabel: "Open marketing account",
      icon: "📣",
    },
    {
      key: "membership-revenue",
      tone: "violet",
      title: "Membership revenue",
      meta: `This month (${monthLabel})`,
      noty: summary?.membershipRevenue?.totalNoty,
      cad: summary?.membershipRevenue?.totalCad,
      link: "/admin/finance/accounts/membership-revenue",
      linkLabel: "Open revenue account",
      icon: "💎",
    },
    {
      key: "wallet-liability",
      tone: "gold",
      title: "Wallet liability",
      meta: "Current balance",
      noty: summary?.walletLiability?.balanceNoty,
      cad: summary?.walletLiability?.balanceCad,
      link: "/admin/finance/accounts/wallet-liability",
      linkLabel: "Open liability account",
      icon: "👛",
    },
  ];

  return (
    <div className={base.page}>
      <header className={base.header}>
        <div className={base.headerLeft}>
          <p className={base.eyebrow}>Finance • Accounts</p>
          <h1 className={base.title}>All accounts</h1>
          <p className={base.subtitle}>
            Review current balances and month-to-date totals across core
            accounts.
          </p>

          <div className={cls.headerChips}>
            <span className={cls.chip}>
              <span className={cls.chipDot} />
              Live snapshot
            </span>
            <span className={`${cls.chip} ${cls.chipSoft}`}>
              Updated: {monthLabel}
            </span>
          </div>
        </div>
      </header>

      <section className={base.card}>
        <div className={cls.sectionTop}>
          <h2 className={base.sectionTitle}>Account snapshot</h2>
          <div className={cls.sectionHint}>
            Tap any card to open its ledger.
          </div>
        </div>

        {error ? (
          <div className={`${base.stateBox} ${base.stateBoxError}`}>
            <p>{error}</p>
          </div>
        ) : null}

        {loading ? (
          <div className={cls.skeletonGrid} aria-busy="true" aria-live="polite">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={cls.skeletonCard}>
                <div className={cls.skTop}>
                  <div className={cls.skIcon} />
                  <div className={cls.skTitle} />
                </div>
                <div className={cls.skMeta} />
                <div className={cls.skRows}>
                  <div className={cls.skRow} />
                  <div className={cls.skRow} />
                </div>
                <div className={cls.skLink} />
              </div>
            ))}
          </div>
        ) : (
          <div className={cls.grid}>
            {cards.map((card) => (
              <article
                key={card.key}
                className={`${cls.accountCard} ${cls[`tone_${card.tone}`]}`}
              >
                <div className={cls.cardHead}>
                  <div className={cls.iconBadge} aria-hidden="true">
                    {card.icon}
                  </div>

                  <div className={cls.headText}>
                    <h3 className={cls.cardTitle}>{card.title}</h3>
                    <p className={cls.cardMeta}>{card.meta}</p>
                  </div>
                </div>

                <div className={cls.amounts}>
                  <div className={cls.amountRow}>
                    <span className={cls.amountLabel}>Noty</span>
                    <span className={cls.amountValue}>
                      {formatAmount(card.noty)}
                    </span>
                  </div>

                  <div className={cls.amountRow}>
                    <span className={cls.amountLabel}>CAD</span>
                    <span className={cls.amountValue}>
                      {formatAmount(card.cad)}
                    </span>
                  </div>
                </div>

                <Link className={cls.cardLink} to={card.link}>
                  {card.linkLabel}
                  <span className={cls.arrow} aria-hidden="true">
                    →
                  </span>
                </Link>

                <div className={cls.cornerGlow} aria-hidden="true" />
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AllAccounts;
